import { FC } from 'react';
import { Link as _Link, useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useReactiveVar, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import { PageTitle } from '../component/primitive/TypographyUI';
import { KaizenChartForHooks, Chart, RequestVariables } from '@kaizenplatform/kaizen-chartjs';
import { SPACING_LARGE, SPACING_MEDIUM, SPACING_SMALL } from '../constant/spacing';
import { GetChartDocument, GetChartQueryVariables } from '../../generated/graphql';
import { lastAccessedOrganizationVar } from '../../cache';

const SegmentPage: FC = () => {
  const navigate = useNavigate();
  const location: Exclude<Location, 'state'> & { state: any } = useLocation();
  const org = useReactiveVar(lastAccessedOrganizationVar)!;
  const scope = { from: '2021-01-01', to: '2022-01-31' };
  const identifier = {
    id: `planbcd_segsum_${org.id}`,
    identifierType: 'resource_key',
  };

  const defaultDate = new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 2).toISOString().split('T')[0];
  const { data, error, loading, refetch } = useQuery<any, GetChartQueryVariables>(GetChartDocument, {
    context: { clientName: 'dataplatform' },
    fetchPolicy: 'cache-first',
    variables: RequestVariables.fromJSON({
      ...identifier,
      scope: scope,
      breakdowns: [
        {
          breakdown: 'date',
          filter: [defaultDate],
        },
      ],
    }),
  });

  const OnClickBack = () => {
    if (location.state.prevPath && location.state.prevPath !== location.pathname) {
      navigate(-1);
    } else {
      navigate('/engine/sitedock-v2');
    }
  };

  if (loading)
    return (
      <Root>
        <TitleFlex>
          <BackButton onClick={OnClickBack} />
          <PageTitle flexGrow variant="h2">
            カスタムセグメント: 日次サマリー
          </PageTitle>
        </TitleFlex>
        <Container>Loading...</Container>
      </Root>
    );
  if (error)
    return (
      <Root>
        <TitleFlex>
          <BackButton onClick={OnClickBack} />
          <PageTitle flexGrow variant="h2">
            カスタムセグメント: 日次サマリー
          </PageTitle>
        </TitleFlex>
        <Container>データがまだありません: {error?.message}</Container>
      </Root>
    );

  return (
    <Root>
      <TitleFlex>
        <BackButton onClick={OnClickBack} />
        <PageTitle flexGrow variant="h2">
          カスタムセグメント: 日次サマリー
        </PageTitle>
      </TitleFlex>
      <Container>
        <style>{hideStyle}</style>
        <KaizenChartForHooks
          autoHeight={true}
          chartData={data ? Chart.fromJSON(data.chart) : null}
          identifier={identifier}
          scope={scope}
          tableOptions={{
            pagination: true,
          }}
          loading={loading}
          refetch={refetch}
        />
      </Container>
    </Root>
  );
};

const Root = styled('div')({
  padding: '0 24px',
});

const Container = styled('div')({
  padding: SPACING_MEDIUM,
  backgroundColor: 'rgba(255, 255, 255, 1)',
  boxShadow: '0px 1px 9px rgba(0, 0, 0, 0.04)',
  borderRadius: '8px',
});
const TitleFlex = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const BackButton = styled(ArrowBackIcon)({
  flexGrow: 0,
  flexShrink: 0,
  marginTop: SPACING_LARGE,
  marginBottom: SPACING_MEDIUM,
  marginRight: SPACING_SMALL,
  cursor: 'pointer',
});

// tmp solution for hiding 'date' column
// Kaizen Chart does not have a way to hide breakdown filter column for a table
const hideStyle = `
  .MuiTableHead-root th:last-child, .MuiTableBody-root td:last-child {
    display: none;
  }
`;

export default SegmentPage;
